/*
Example usage:

const events = [
    {
        id: 'vet-clinic-5',
        title: 'Vet Clinic',
        location: 'Kahoots Solana Beach',
        date: '2025-04-10',
        time: '9:00am to 12:00pm',
        type: 'workshop'
    },
    {
        id: 'webinar-1',
        title: 'Pet Nutrition Webinar',
        location: 'Online',
        date: '2025-05-15',
        time: '3:00pm to 4:00pm',
        type: 'webinar'
    }
];

const eventsList = new EventsList(events);
eventsList.render('#events-container');

// To rebuild with new events:
const newEvents = [
    {
        id: 'training-1',
        title: 'Dog Training Workshop',
        location: 'Kahoots Poway',
        date: '2025-06-20',
        time: '10:00am to 1:00pm',
        type: 'workshop'
    }
];
eventsList.rebuild(newEvents);
eventsList.render('#events-container');
*/

class EventsList {
  constructor(events = [], flyers = []) {
    // Filter events to only include those starting from today and sort by start date
    const today = new Date();
    this.events = events
      .filter((event) => new Date(event.start) >= today)
      .sort((a, b) => new Date(a.start) - new Date(b.start));
    this.currentPage = 1;
    this.eventsPerPage = 5;
    this.flyers = flyers;
    this.flyersBuilt = false;
  }

  buildFlyersHTML(flyer) {
    try {
      const container = document.getElementById('flyers');
      if (!container) {
        console.error('Flyer container not found');
        return;
      }
      container.innerHTML = ''; // Clear existing content

      // Loop through each flyer and build the HTML
      this.flyers.forEach((flyer) => {
        let flyerContainer;
        const flyerHandle = flyer.title.replace(/\s+/g, '-').toLowerCase();

        /**
         * Build flyer container
         */
        flyerContainer = document.createElement('div');
        flyerContainer.className = 'flyer-content';
        flyerContainer.setAttribute('aria-label', `${flyer.title} flyer`);
        flyerContainer.setAttribute('aria-hidden', 'true'); // Accessibility
        flyerContainer.setAttribute('aria-live', 'assertive');
        flyerContainer.setAttribute('data-displayed', 'false');
        flyerContainer.setAttribute('data-flyer-id', flyerHandle);
        flyerContainer.tabIndex = 0;
        container.appendChild(flyerContainer);

        /**
         * Build flyer content
         */
        const closeButton = document.createElement('button');
        closeButton.className = 'close-btn';
        closeButton.setAttribute('aria-hidden', 'false');
        closeButton.setAttribute('title', 'close event flyer');
        closeButton.innerHTML = 'Close Flyer <span class="close-icon"><span></span><span></span></span>';
        flyerContainer.appendChild(closeButton);

        /**
         * Build flyer image
         * - Use the first image in the flyer object
         */
        const imgContainer = document.createElement('div');
        imgContainer.className = 'img-container';
        const img = document.createElement('img');
        img.src = flyer.image[0].url;
        img.alt = flyer.image[0].alt;
        img.loading = 'lazy';
        img.width = 300;
        img.height = 180;
        imgContainer.appendChild(img);
        flyerContainer.appendChild(imgContainer);

        /**
         * Build flyer title
         * - Use the title from the flyer object
         */
        const flyerContent = document.createElement('div');
        flyerContent.setAttribute('aria-label', `About ${flyer.title}`);
        flyerContent.innerHTML = flyer.detailed_content;
        flyerContainer.appendChild(flyerContent);

        /**
         * Build flyer bottom close buttons
         */
        const bottomCloseButtons = document.createElement('div');
        bottomCloseButtons.className = 'close-buttons bottom-close-buttons';

        /**
         * Build flyer close button
         */
        const viewEventsButton = document.createElement('button');
        viewEventsButton.className = 'button btn kah-btn';
        viewEventsButton.setAttribute('aria-label', 'View Events');
        viewEventsButton.setAttribute('title', 'View Events');
        viewEventsButton.innerHTML = '<span>View Events</span>';
        viewEventsButton.addEventListener('click', () => {
          // close the flyer using closeFlyer method
          const flyerId = flyerContainer.getAttribute('data-flyer-id');
          this.closeFlyer(flyerId);
        });

        bottomCloseButtons.appendChild(viewEventsButton);

        /**
         * Build flyer locations button
         */
        const locationsButton = document.createElement('button');
        locationsButton.className = 'button button--secondary';
        locationsButton.id = 'locations-button';
        locationsButton.setAttribute('aria-label', 'View Locations');
        locationsButton.setAttribute('title', 'View Locations');
        locationsButton.innerHTML = '<span>Find your location</span>';
        locationsButton.addEventListener('click', () => {
          // open a new tab at the locations page /pages/locations
          window.open('/pages/locations', '_blank');
        });
        bottomCloseButtons.appendChild(locationsButton);

        /**
         * Append flyer close buttons to flyer container
         */
        flyerContainer.appendChild(bottomCloseButtons);

        /**
         * Append flyerContainer to container
         */
        container.appendChild(flyerContainer);
      });

      this.flyersBuilt = true;
    } catch (error) {
      console.error('Error building flyer HTML:', error);
    }
  }

  buildEventHTML(event) {
    const { id, name, location, start, end, type, media, details, host } = event;

    const startDate = new Date(start);
    const day = startDate.getDate();
    const month = startDate.getMonth();
    const year = startDate.getFullYear();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const formattedDate = `${monthNames[month]}<span>${day}</span>`;

    const eventContainer = document.createElement('div');
    eventContainer.className = 'event';
    eventContainer.setAttribute('aria-label', `${name} event at ${location}`);
    eventContainer.id = id;

    const mainEventContent = document.createElement('div');
    mainEventContent.className = 'main-event-content';
    eventContainer.appendChild(mainEventContent);

    const dateDiv = document.createElement('div');
    dateDiv.className = 'date';
    dateDiv.setAttribute('aria-label', `event date: ${formattedDate}`);
    dateDiv.setAttribute('data-day', day);
    dateDiv.setAttribute('data-month', month + 1);
    dateDiv.setAttribute('data-year', year);
    dateDiv.innerHTML = formattedDate;
    mainEventContent.appendChild(dateDiv);

    const detailsDiv = document.createElement('div');
    detailsDiv.className = 'details';

    const title = document.createElement('h3');
    title.innerHTML = `${name}<span> at ${location}</span>`;
    detailsDiv.appendChild(title);

    const time = document.createElement('p');
    time.textContent = `${startDate.toLocaleTimeString([], { hour: 'numeric', minute: 'numeric' })} to ${new Date(
      end
    ).toLocaleTimeString([], { hour: 'numeric', minute: 'numeric' })}`;
    detailsDiv.appendChild(time);

    mainEventContent.appendChild(detailsDiv);

    const actionsDiv = document.createElement('div');
    actionsDiv.className = 'actions';
    mainEventContent.appendChild(actionsDiv);

    // Add store details button
    /*
     * - 4s ranch
     */
    const getLocationUrl = (host) => {
      let handle = host.replace(/\s+/g, '-').toLowerCase();
      const base = '/pages/kahoots-';
      switch (handle) {
        case '4s-ranch':
          handle = '4s';
          break;
        default:
          handle = handle;
          break;
      }
      const url = `${base}${handle}`;
      return url;
    };
    const storeDetailsButton = document.createElement('a');
    storeDetailsButton.className = '';
    storeDetailsButton.classList.add('btn', 'kah-btn', 'store-details-toggle');
    storeDetailsButton.setAttribute('aria-label', `View store details for ${host}`);
    storeDetailsButton.setAttribute('data-store-id', host.replace(/\s+/g, '-').toLowerCase());
    const storeDetailsButtonInner = document.createElement('span');
    storeDetailsButtonInner.textContent = `Store Details`;
    storeDetailsButton.appendChild(storeDetailsButtonInner);
    const locationUrl = getLocationUrl(host);
    storeDetailsButton.href = locationUrl;
    actionsDiv.appendChild(storeDetailsButton);

    if (media?.length || details) {
      const contentDiv = document.createElement('div');
      contentDiv.className = 'content collapsed';
      contentDiv.setAttribute('aria-hidden', 'true'); // Accessibility

      if (media?.length) {
        const image = document.createElement('img');
        image.src = media[0].url || '';
        image.alt = media[0].alt || '';
        contentDiv.appendChild(image);
      }

      if (!!details) {
        const content = document.createElement('div');
        content.innerHTML = details;
        contentDiv.appendChild(content);

        // Add "details" button
        const detailsButton = document.createElement('button');
        detailsButton.className = 'details-toggle';
        detailsButton.setAttribute('aria-expanded', 'false');
        detailsButton.setAttribute('aria-controls', 'details');
        detailsButton.setAttribute('aria-label', 'Show event details');
        detailsButton.textContent = 'Event Details';

        // Add event listener for details button
        actionsDiv.appendChild(detailsButton);
      }

      eventContainer.appendChild(contentDiv);
    }

    // Add flyer button
    const matchingFlyer = this.flyers.find((flyer) => {
      const flyerHandle = flyer.title.replace(/\s+/g, '-').toLowerCase();
      const eventTypeHandle = type.replace(/\s+/g, '-').toLowerCase();
      return flyerHandle.includes(eventTypeHandle);
    });

    if (!!matchingFlyer) {
      const flyerButton = document.createElement('button');
      flyerButton.className = 'flyer-toggle';
      flyerButton.setAttribute('aria-label', `View flyer for ${matchingFlyer.title}`);
      flyerButton.setAttribute('data-flyer-id', matchingFlyer.title.replace(/\s+/g, '-').toLowerCase());
      flyerButton.textContent = matchingFlyer.cta_text || 'View Flyer';
      actionsDiv.appendChild(flyerButton);
    }

    return eventContainer.outerHTML;
  }

  render(selector) {
    // Get the container element
    if (!selector) {
      console.error('Selector is required to render events.');
      return;
    }

    const container = document.querySelector(selector);

    if (!container) {
      console.error(`Selector "${selector}" not found.`);
      return;
    }

    // Build flyers html
    if (!this.flyersBuilt) {
      this.buildFlyersHTML(this.flyers);
    }

    // Build events html
    const start = (this.currentPage - 1) * this.eventsPerPage;
    const end = start + this.eventsPerPage;
    const paginatedEvents = this.events.slice(start, end);
    const eventsHTML = paginatedEvents.map((event) => this.buildEventHTML(event)).join('');
    container.innerHTML = '';
    container.appendChild(document.createRange().createContextualFragment(eventsHTML));

    // Add event listeners for "details" button
    document.querySelectorAll('.details-toggle').forEach((button) => {
      button.addEventListener('click', (event) => {
        const contentDiv = event.target.closest('.event').querySelector('.content');
        const actionsDiv = event.target.closest('.actions');
        const isExpanded = event.target.getAttribute('aria-expanded') === 'true';

        event.target.setAttribute('aria-expanded', !isExpanded);
        event.target.textContent = isExpanded ? 'Event Details' : 'Close';
        contentDiv.setAttribute('aria-hidden', isExpanded);

        if (!isExpanded) {
          contentDiv.style.height = `${contentDiv.scrollHeight + 20}px`; // Expand
          contentDiv.classList.remove('collapsed');
          contentDiv.classList.add('expanded');
        } else {
          contentDiv.style.height = '0'; // Collapse
          contentDiv.classList.remove('expanded');
          contentDiv.classList.add('collapsed');
        }

        actionsDiv.classList.toggle('always-visible', !isExpanded); // Keep actions visible
      });
    });

    // Add event listeners for "flyer" button
    document.querySelectorAll('.flyer-toggle').forEach((button) => {
      button.addEventListener('click', (event) => {
        const $target = event.target.closest('.flyer-toggle');
        const flyerId = $target.getAttribute('data-flyer-id');
        this.openFlyer(flyerId);
      });
    });

    document.querySelectorAll('.flyer-content .close-btn').forEach((button) => {
      button.addEventListener('click', (event) => {
        const flyerContainer = event.target.closest('.flyer-content');
        const flyerId = flyerContainer.getAttribute('data-flyer-id');
        this.closeFlyer(flyerId);
      });
    });

    // Handle flyer open based on search params
    const params = new URLSearchParams(window.location.search);
    const flyerId = params.get('flyerOpen');
    if (flyerId) {
      this.openFlyer(flyerId);
    }

    // Add pagination controls
    this.renderPaginationControls(container);
  }

  openFlyer(flyerId) {
    // if (!!window.location.search.includes('flyerOpen')) {
    //   return; // If a flyer is already open, do nothing
    // }

    const flyers = document.getElementById('flyers');
    const flyerContainer = document.querySelector(`.flyer-content[data-flyer-id="${flyerId}"]`);
    const flyerContainerSiblings = document.querySelectorAll('.flyer-content');
    const eventsList = document.querySelector('#events-list');
    const eventsCal = document.querySelector('#events-calendar');
    const eventsSummary = document.querySelector('#filter-summary');
    const eventsListContainer = document.querySelector('.events-container');

    // Hide the events list and calendar
    if (eventsList) {
      eventsList.setAttribute('aria-hidden', 'true');
      eventsList.setAttribute('data-displayed', 'false');
    }
    if (eventsCal) {
      eventsCal.setAttribute('aria-hidden', 'true');
      eventsCal.setAttribute('data-displayed', 'false');
    }
    if (eventsSummary) {
      eventsSummary.setAttribute('aria-hidden', 'true');
      eventsSummary.setAttribute('data-displayed', 'false');
    }
    if (eventsListContainer) {
      eventsListContainer.setAttribute('aria-hidden', 'true');
      eventsListContainer.setAttribute('data-displayed', 'false');
    }

    // Hide all other flyers
    flyerContainerSiblings.forEach((sibling) => {
      sibling.setAttribute('aria-hidden', 'true');
      sibling.setAttribute('data-displayed', 'false');
    });

    // Display the flyer container
    flyers.setAttribute('aria-hidden', 'false');
    flyers.setAttribute('data-displayed', 'true');

    // Display the selected flyer
    flyerContainer.setAttribute('aria-hidden', 'false');
    flyerContainer.setAttribute('data-displayed', 'true');
    flyerContainer.focus();
    flyerContainer.scrollIntoView({ behavior: 'smooth' });

    this.updateSearchParams('flyerOpen', flyerId);
  }

  closeFlyer(flyerId) {
    let flyerToClose =
      flyerId || document.querySelector(`.flyer-content[data-displayed="true"]`)?.dataset.flyerId || false;

    if (!flyerToClose) {
      return; // If no flyer is open, do nothing
    }

    const flyerContainer = document.querySelector(`.flyer-content[data-flyer-id="${flyerToClose}"]`);
    flyerContainer.setAttribute('aria-hidden', 'true');
    flyerContainer.setAttribute('data-displayed', 'false');

    const flyers = document.getElementById('flyers');
    flyers.setAttribute('aria-hidden', 'true');
    flyers.setAttribute('data-displayed', 'false');

    // Show the events list and calendar
    const eventsList = document.querySelector('#events-list');
    const eventsCal = document.querySelector('#events-calendar');
    if (eventsList) {
      eventsList.setAttribute('aria-hidden', 'false');
      eventsList.setAttribute('data-displayed', 'true');
    }
    if (eventsCal) {
      eventsCal.setAttribute('aria-hidden', 'false');
      eventsCal.setAttribute('data-displayed', 'true');
    }
    const eventsSummary = document.querySelector('#filter-summary');
    if (eventsSummary) {
      eventsSummary.setAttribute('aria-hidden', 'false');
      eventsSummary.setAttribute('data-displayed', 'true');
    }
    const eventsListContainer = document.querySelector('.events-container');
    if (eventsListContainer) {
      eventsListContainer.setAttribute('aria-hidden', 'false');
      eventsListContainer.setAttribute('data-displayed', 'true');
    }

    const eventsDiv = document.querySelector('#events');
    if (eventsDiv) {
      eventsDiv.scrollIntoView({ behavior: 'smooth' });
    }

    // Reset the flyer open state in the URL
    this.updateSearchParams('flyerOpen', null);
  }

  updateSearchParams(key, value) {
    const params = new URLSearchParams(window.location.search);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    window.history.pushState({}, '', `${window.location.pathname}?${params.toString()}`);
  }

  renderPaginationControls(container) {
    const totalPages = Math.ceil(this.events.length / this.eventsPerPage);
    let paginationHTML = `<div class="pagination" aria-label="Pagination controls">`;

    // Add left arrow
    if (this.currentPage > 1) {
      paginationHTML += `
                <button aria-label="Previous page" class="page-arrow" data-page="${this.currentPage - 1}">
                    &laquo;
                </button>
            `;
    }

    // Determine page range to display
    const maxVisiblePages = 5;
    let startPage = Math.max(1, this.currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Add ellipsis on the left if needed
    if (startPage > 1) {
      paginationHTML += `
                <button aria-label="More pages before" class="ellipsis" disabled>
                    <span>
                        &hellip;
                    </span>
                </button>
            `;
    }

    // Add page numbers
    for (let i = startPage; i <= endPage; i++) {
      paginationHTML += `
                <button aria-label="Page ${i}" class="page-number ${
        i === this.currentPage ? 'active' : ''
      }" data-page="${i}">
                    <span>
                        ${i}
                    </span>
                </button>
            `;
    }

    // Add ellipsis on the right if needed
    if (endPage < totalPages) {
      paginationHTML += `
                <button aria-label="More pages after" class="ellipsis" disabled>
                    &hellip;
                </button>
            `;
    }

    // Add right arrow
    if (this.currentPage < totalPages) {
      paginationHTML += `
                <button aria-label="Next page" class="page-arrow" data-page="${this.currentPage + 1}">
                    <span>
                        &raquo;
                    </span>
                </button>
            `;
    }

    paginationHTML += `</div>`;
    container.insertAdjacentHTML('beforeend', paginationHTML);

    // Add event listeners for pagination
    container.querySelectorAll('.page-number, .page-arrow').forEach((button) => {
      button.addEventListener('click', (event) => {
        this.currentPage = parseInt(event.target.dataset.page, 10);
        this.render(`#${container.id}`); // Ensure the correct selector is passed

        // Trigger page change callback
        if (this.onPageChange) {
          this.onPageChange(this.currentPage);
        }
      });
    });
  }

  rebuild(newEvents) {
    this.events = newEvents;
    this.currentPage = 1;
  }
}
